<template>
  <tr :id="'r-' + item.ID">
    <td v-for="header in visibleHeaders" :key="header.value" :class="header.align ? `text-${header.align}` : ''">
      <!-- Transaction Code -->
      <template v-if="header.value === 'TransactionCode'">
        {{ item.TransactionCode }}
      </template>
      
      <!-- Product Type with edit -->
      <template v-else-if="header.value === 'ProductType'">
        <v-edit-dialog 
          v-if="isEditable"
          :return-value.sync="item.ProductType" 
          large lazy persistent 
          @save="$emit('save-item', {'ID': item.ID, 'ProductType': item.ProductType})"
        >
          <div>{{ item.ProductType | display_value(productTypeList) }}</div>
          <template v-slot:input>
            <v-select 
              v-model="item.ProductType" 
              :items="productTypeList" 
              item-text="name" 
              item-value="value" 
              label="Loại hàng" 
              single-line 
              autofocus
            ></v-select>
          </template>
        </v-edit-dialog>
        <div v-else>{{ item.ProductType | display_value(productTypeList) }}</div>
      </template>
      
      <!-- Weight with edit -->
      <template v-else-if="header.value === 'Weight'">
        <v-edit-dialog 
          v-if="weightEditable"
          :return-value.sync="item.Weight" 
          large lazy persistent 
          @save="$emit('save-item', {'ID': item.ID, 'Weight': item.Weight})"
        >
          <div>{{ item.Weight | currency('', 2) }}</div>
          <template v-slot:input>
            <v-text-field 
              v-model="item.Weight" 
              type="number" 
              step="0.01" 
              label="Cân nặng" 
              single-line 
              autofocus
            ></v-text-field>
          </template>
        </v-edit-dialog>
        <div v-else>{{ item.Weight | currency('', 2) }}</div>
      </template>
      
      <!-- Fee with edit -->
      <template v-else-if="header.value === 'FeeShipVND'">
        <v-edit-dialog 
          v-if="item.Status != packageStatusEnum.DA_XUAT_VN"
          :return-value.sync="item.FeeShipVND" 
          large lazy persistent 
          @save="$emit('save-item', {'ID': item.ID, 'FeeShipVND': item.FeeShipVND})"
        >
          <div>{{ item.FeeShipVND | currency }}</div>
          <template v-slot:input>
            <v-text-field 
              v-model="item.FeeShipVND" 
              type="number" 
              step="1" 
              label="Phí khác" 
              single-line 
              autofocus
            ></v-text-field>
          </template>
        </v-edit-dialog>
        <div v-else>{{ item.FeeShipVND | currency('') }}</div>
      </template>
      
      <!-- Status with chip -->
      <template v-else-if="header.value === 'Status'">
        <v-chip 
          small 
          :color="getStatusColor(item.Status, packageStatus)" 
          text-color="white"
        >
          {{ item.Status | display_value(packageStatus) }}
        </v-chip>
      </template>
      
      <!-- Notes with tooltip -->
      <template v-else-if="header.value === 'StoreCNNote' || header.value === 'StoreVNNote'">
        <v-tooltip top max-width="250">
          <template v-slot:activator="{ on }">
            <span v-on="on" class="truncate">{{ item[header.value] }}</span>
          </template>
          <span>{{ item[header.value] }}</span>
        </v-tooltip>
      </template>
      
      <!-- Actions column -->
      <template v-else-if="header.value === ''">
        <v-menu offset-y>
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on">
              <v-icon>fa-ellipsis-v</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item @click="$emit('goto-detail', item.PackageType, item.OrderID, item.UID)" v-if="item.OrderID > 0">
              <v-list-item-title>Chi tiết đơn hàng</v-list-item-title>
            </v-list-item>
            <v-list-item @click="$emit('print-transaction', item)" v-if="canPrint">
              <v-list-item-title>In tem</v-list-item-title>
            </v-list-item>
            <v-list-item @click="$emit('split-package', item.ID)" v-if="canSplit">
              <v-list-item-title>Tách kiện</v-list-item-title>
            </v-list-item>
            <v-list-item @click="$emit('delete-item', item)" v-if="canDelete">
              <v-list-item-title class="red--text">Xóa</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
      
      <!-- Date fields -->
      <template v-else-if="header.value.includes('Date')">
        {{ item[header.value] | display_date }}
      </template>
      
      <!-- Volume with edit -->
      <template v-else-if="header.value === 'Volume'">
        <v-edit-dialog 
          v-if="weightEditable"
          large lazy persistent 
          @save="$emit('save-item', {'ID': item.ID, 'Length': item.Length, 'Width': item.Width, 'Height': item.Height})"
        >
          <div>
            {{ item.Volume | currency('', 3) }}
            <v-divider></v-divider>
            {{ item.Length | currency('', 1)}} x {{ item.Width | currency('', 1)}} x {{ item.Height | currency('', 1)}}
          </div>
          <template v-slot:input>
            <v-text-field v-model="item.Length" type="number" step=".1" label="Dài (cm)" single-line autofocus></v-text-field>
            <v-text-field v-model="item.Width" type="number" step=".1" label="Rộng (cm)" single-line></v-text-field>
            <v-text-field v-model="item.Height" type="number" step=".1" label="Cao (cm)" single-line></v-text-field>
          </template>
        </v-edit-dialog>
        <div v-else>{{ item.Volume | currency('', 3) }}</div>
      </template>
      <template v-else-if="header.value === 'TotalWeight'">
        {{ item[header.value] | currency('', 2) }}
      </template>
      <template v-else-if="header.value === 'TotalVolume'">
        {{ item[header.value] | currency('', 3) }}
      </template>
      <!-- Price fields with edit -->
      <template v-else-if="header.value.includes('Price') || header.value.includes('Cost') || header.value.includes('Amount') || header.value.includes('Profit')">
        <v-edit-dialog 
          v-if="isPriceEditable(header.value)"
          :return-value.sync="item[header.value]" 
          large lazy persistent 
          @save="$emit('save-item', {'ID': item.ID, [header.value]: item[header.value]})"
        >
          <div>{{ item[header.value] | currency }}</div>
          <template v-slot:input>
            <v-text-field 
              v-model="item[header.value]" 
              type="number" 
              step="1" 
              :label="header.text" 
              single-line 
              autofocus
            ></v-text-field>
          </template>
        </v-edit-dialog>
        <div v-else>{{ item[header.value] | currency }}</div>
      </template>
      <template v-else-if="header.value === 'RealNumberOfPack'">
        {{ item[header.value] | currency }}
      </template>
      
      <!-- Enum fields -->
      <template v-else-if="header.value === 'FeeType'">
        {{ item.FeeType | display_value(feeTypeEnum) }}
      </template>
      
      <!-- Default -->
      <template v-else>
        {{ item[header.value] }}
      </template>
    </td>
  </tr>
</template>

<script>
export default {
  name: 'TransactionTableRow',
  props: {
    item: {
      type: Object,
      required: true
    },
    visibleHeaders: {
      type: Array,
      required: true
    },
    productTypeList: {
      type: Array,
      default: () => []
    },
    packageStatus: {
      type: Array,
      default: () => []
    },
    packageStatusEnum: {
      type: Object,
      default: () => ({})
    },
    feeTypeEnum: {
      type: Array,
      default: () => []
    },
    isEditable: {
      type: Boolean,
      default: false
    },
    weightEditable: {
      type: Boolean,
      default: false
    },
    canPrint: {
      type: Boolean,
      default: false
    },
    canSplit: {
      type: Boolean,
      default: false
    },
    canDelete: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    getStatusColor(status, enums) {
      return this.$options.filters.display_color(status, enums);
    },
    isPriceEditable(fieldName) {
      // Define which price fields are editable
      const editableFields = ['WeightPrice', 'VolumePrice', 'WeightCost', 'VolumeCost'];
      return this.isEditable && editableFields.includes(fieldName);
    }
  }
};
</script>
